import { Box, Button, Card, CardContent, CardHeader, Skeleton, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';

import { useGetDocumentSummary } from '@/api/endpoints/document/useGetDocumentSummary';
import { useReprocessDocument } from '@/api/endpoints/document/useReprocessDocument';
import { ZapIcon } from '@/assets/icons/ZapIcon';

interface DocumentSummaryProps {
  documentId: string;
}

const DocumentSummary: React.FC<DocumentSummaryProps> = ({ documentId }) => {
  const summaryQuery = useGetDocumentSummary(documentId);
  const reprocessMutation = useReprocessDocument();

  const handleReprocess = () => {
    reprocessMutation.mutate(documentId);
  };

  if (summaryQuery.isLoading) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardHeader title="Document Summary" />
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Skeleton variant="text" width="100%" height={24} />
            <Skeleton variant="text" width="95%" height={24} />
            <Skeleton variant="text" width="90%" height={24} />
            <Skeleton variant="text" width="85%" height={24} />
            <Skeleton variant="text" width="80%" height={24} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (summaryQuery.isError) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardHeader title="Document Summary" />
        <CardContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Document summary not available.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ZapIcon />}
            onClick={handleReprocess}
            disabled={reprocessMutation.isPending}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            {reprocessMutation.isPending ? 'Processing...' : 'Process Document With AI'}
          </Button>
          {reprocessMutation.isError && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              Failed to reprocess document. Please try again.
            </Typography>
          )}
          {reprocessMutation.isSuccess && (
            <Typography color="success.main" variant="body2" sx={{ mt: 1 }}>
              Document reprocessing started successfully!
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  }

  if (!summaryQuery.data) {
    return null;
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader title="Document Summary" />
      <CardContent>
        <ReactMarkdown>{summaryQuery.data}</ReactMarkdown>
      </CardContent>
    </Card>
  );
};

export default DocumentSummary;
